import { useState } from "react";
import { Trash2, ShieldCheck } from "lucide-react";
import { Label } from "./ui-bits";
import { MARKERS, markerById, classify } from "@/data/markers";
import { api } from "@/lib/auth";
import { daysSinceDose } from "./DoseDiary";

const TONE = { low: "text-amber-300", high: "text-rose", "in-range": "text-teal", unknown: "text-dusk" };
const WORD = { low: "Below range", high: "Above range", "in-range": "In range", unknown: "" };

function Sparkline({ points, marker }) {
  if (points.length < 2) return null;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals, marker.low), max = Math.max(...vals, marker.high);
  const W = 240, H = 56;
  const x = (i) => (i / (points.length - 1)) * (W - 8) + 4;
  const y = (v) => H - 6 - ((v - min) / (max - min || 1)) * (H - 12);
  const d = points.map((p, i) => `${i ? "L" : "M"}${x(i)},${y(p.value)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" aria-hidden="true" data-testid={`sparkline-${marker.id}`}>
      <rect x="0" y={y(marker.high)} width={W} height={Math.max(2, y(marker.low) - y(marker.high))} fill="rgba(0,229,192,0.08)" />
      <path d={d} fill="none" stroke="#00E5C0" strokeWidth="2" strokeLinejoin="round" />
      {points.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.value)} r="3" fill="#07090E" stroke="#00E5C0" strokeWidth="2" />)}
    </svg>
  );
}

export function ResultsBoard({ results, onDelete, canDelete, doses = [] }) {
  const grouped = MARKERS.map((m) => ({ m, items: results.filter((r) => r.marker === m.id) })).filter((g) => g.items.length);
  if (!grouped.length) {
    return <div className="card-line rounded-2xl p-8 text-center text-mute" data-testid="results-empty">No results yet. Add your first blood result below — or your clinic will add verified results after each monitoring test.</div>;
  }
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="results-board">
      {grouped.map(({ m, items }) => {
        const latest = items[items.length - 1];
        const status = classify(m, latest.value);
        return (
          <article key={m.id} className="card-line rounded-2xl p-6" data-testid={`marker-card-${m.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="mono-label text-dusk">{m.name}</p>
                <p className="mt-2"><span className="font-display text-4xl text-bone" data-testid={`marker-latest-${m.id}`}>{latest.value}</span><span className="text-dusk text-sm ml-2">{latest.unit}</span></p>
                <p className={`text-sm mt-1 ${TONE[status]}`} data-testid={`marker-status-${m.id}`}>{WORD[status]} · range {m.low}–{m.high} {m.unit}</p>
              </div>
              <p className="mono-label text-dusk text-right">{latest.taken_at}<br />{latest.verified ? <span className="text-teal inline-flex items-center gap-1 mt-1"><ShieldCheck size={12} /> Clinic verified</span> : <span className="mt-1 inline-block">Self-entered</span>}</p>
            </div>
            <Sparkline points={items} marker={m} />
            <p className="text-xs text-mute leading-relaxed mt-3">{m.note}</p>
            <ul className="mt-4 divide-y divide-line text-xs">
              {items.slice().reverse().map((r) => (
                <li key={r.result_id} className="py-2 flex items-center justify-between gap-3" data-testid={`result-row-${r.result_id}`}>
                  <span className="font-mono text-dusk">{r.taken_at}</span>
                  <span className="text-bone/85 flex-1">{r.value} {r.unit}{r.lab ? <span className="text-dusk"> · {r.lab}</span> : null}{(() => { const d = daysSinceDose(r.taken_at, doses); return d === null ? null : <span className="ml-2 px-1.5 py-0.5 rounded bg-teal/10 text-teal font-mono" data-testid={`post-dose-${r.result_id}`}>{d}d post-dose</span>; })()}</span>
                  {canDelete(r) && (
                    <button type="button" onClick={() => onDelete(r.result_id)} className="text-dusk hover:text-rose transition-colors" aria-label="Delete result" data-testid={`result-delete-${r.result_id}`}><Trash2 size={14} /></button>
                  )}
                </li>
              ))}
            </ul>
          </article>
        );
      })}
    </div>
  );
}

export function AddResultForm({ path, onAdded, title = "Add a blood result" }) {
  const [form, setForm] = useState({ marker: MARKERS[0].id, value: "", taken_at: "", lab: "", note: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const marker = markerById(form.marker);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const doc = await api(path, { method: "POST", body: { ...form, value: Number(form.value), unit: marker.unit, lab: form.lab || null, note: form.note || null } });
      onAdded(doc);
      setForm((f) => ({ ...f, value: "", note: "" }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-line rounded-2xl p-6 sm:p-8 space-y-4" data-testid="add-result-form">
      <Label>{title}</Label>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="mono-label text-dusk block mb-2" htmlFor="ar-marker">Marker</label>
          <select id="ar-marker" className="field" value={form.marker} onChange={set("marker")} data-testid="add-result-marker">
            {MARKERS.map((m) => <option key={m.id} value={m.id} className="bg-surface">{m.name}</option>)}
          </select>
        </div>
        <div>
          <label className="mono-label text-dusk block mb-2" htmlFor="ar-value">Value ({marker.unit})</label>
          <input id="ar-value" type="number" step="any" min="0" required className="field" value={form.value} onChange={set("value")} data-testid="add-result-value" />
        </div>
        <div>
          <label className="mono-label text-dusk block mb-2" htmlFor="ar-date">Sample date</label>
          <input id="ar-date" type="date" required className="field" value={form.taken_at} onChange={set("taken_at")} data-testid="add-result-date" />
        </div>
        <div>
          <label className="mono-label text-dusk block mb-2" htmlFor="ar-lab">Laboratory / source (optional)</label>
          <input id="ar-lab" className="field" value={form.lab} onChange={set("lab")} placeholder="e.g. Clinic panel, home test" data-testid="add-result-lab" />
        </div>
      </div>
      {error && <p className="text-rose text-sm" data-testid="add-result-error">{error}</p>}
      <button type="submit" disabled={busy} className="btn-primary" data-testid="add-result-submit">{busy ? "Saving…" : "Save result"}</button>
    </form>
  );
}
